import { useQueryClient } from "@tanstack/react-query";
import { notificacion, notificacionError } from "@/components/Alerts";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "@/components/modals/BaseModal";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import type { Respuesta } from "@/ts/typings/clases";
import { keysColumn, useEstanteria } from "../GestionUbicaciones";
import { useCallback, useEffect, useMemo, useState } from "react";

interface ModalEditarFilaProps extends BaseModalProps {
	readonly currentZona: string;
	readonly currentEstanteria: string;
}
export function ModalAddUbicaciones(props: ModalEditarFilaProps) {
	const { close } = props;
	const queryClient = useQueryClient();
	const [config, setConfig] = useState({
		zona: props.currentZona,
		estanteria: props.currentEstanteria,
		columnas: "A-A",
		alturas: "01-01"
	});
	const [excluded, setExcluded] = useState<string[]>([]);

	const ubicacionesExistentes = useEstanteria(config.zona, config.estanteria);

	useEffect(() => {
		let minCol: string | null = null;
		let maxCol: string | null = null;
		let minAlt: number | null = null;
		let maxAlt: number | null = null;
		for (const ubicacion of ubicacionesExistentes.data ?? []) {
			if (minCol == null || ubicacion.idColumna < minCol) minCol = ubicacion.idColumna;
			if (maxCol == null || ubicacion.idColumna > maxCol) maxCol = ubicacion.idColumna;
			const altNum = Number(ubicacion.idAltura);
			if (minAlt == null || altNum < minAlt) minAlt = altNum;
			if (maxAlt == null || altNum > maxAlt) maxAlt = altNum;
		}
		minCol ??= "A";
		maxCol ??= "A";
		minAlt ??= 1;
		maxAlt ??= 1;

		setConfig(old => ({
			...old,
			columnas: minCol === "L" ? "A-A" : `${minCol}-${maxCol}`,
			alturas: minAlt === 99 ? "01-01" : `${minAlt}-${maxAlt}`,
		}));

		const defaultExcluded = new Array<string>();
		const minColIndex = keysColumn.indexOf(minCol);
		const maxColIndex = keysColumn.indexOf(maxCol);
		if (minColIndex === -1 || maxColIndex === -1 || minColIndex > maxColIndex) return;


		for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
			for (let rowIndex = minAlt; rowIndex <= maxAlt; rowIndex++) {
				const idUbicacion = `${config.zona}${config.estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;
				const existingLocations = ubicacionesExistentes.data ?? [];

				if (existingLocations.some(ubi => ubi.idUbicacion === idUbicacion) == false) {
					defaultExcluded.push(idUbicacion);
				}
			}
		}


		setExcluded(defaultExcluded);
	}, [ubicacionesExistentes.data, config.zona, config.estanteria]);

	useEffect(() => {
		setConfig(old => ({
			...old,
			zona: props.currentZona,
			estanteria: props.currentEstanteria,
		}));
	}, [props.currentEstanteria, props.currentZona]);

	const ubicacionesCalientes = useMemo(() => {
		if (ubicacionesExistentes.data == null) return null;
		return getUbicacionesVisualizar(config, excluded, ubicacionesExistentes.data);
	}, [config, excluded, ubicacionesExistentes.data]);

	const guardarUbicacion = useCallback((event: React.SubmitEvent) => {
		event.preventDefault();
		if (ubicacionesCalientes == null) throw new Error("No hay ubicaciones para guardar.");

		const ubicaciones = ubicacionesCalientes
			.filter(ubi => (ubi.nueva == true) && (ubi.excluded == false))
			.map(ubi => ({
				idUbicacion: ubi.idUbicacion,
				idAlmacen: ubi.idAlmacen,
				idEstanteria: ubi.idEstanteria,
				idColumna: ubi.idColumna,
				idAltura: ubi.idAltura,
			}));

		void sendAjaxRequest<Respuesta>("POST", "/api/v1/insertUbicaciones", { ubicaciones })
			.then(response => {
				if (response.tipo == "error") {
					notificacionError(response.message ?? "Error al guardar la ubicación.");
					return;
				}

				notificacion(response.message ?? "Ubicación guardada con éxito.");
				close();
				void queryClient.invalidateQueries({ queryKey: ["getEstanterias"] });
				void queryClient.invalidateQueries({ queryKey: ["getEstanteria", config.zona, config.estanteria] });
			});
	}, [close, queryClient, config, ubicacionesCalientes]);

	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Ubicación</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<form onSubmit={guardarUbicacion}>
				<div className="modal-body">
					<div style={{ display: "flex", justifyContent: "space-between", gap: "15px" }}>
						<FormNuevaUbicacion config={config} setConfig={setConfig} />
						{ubicacionesCalientes != null && <Estanteria zona={config.zona} fila={config.estanteria} ubicaciones={ubicacionesCalientes} setExcluded={setExcluded} />}
						{ubicacionesCalientes == null && <p>Configuracion incorrecta. Las filas deben de estar en el rango 01-99 y las columnas en el rango A-L.</p>}
					</div>
				</div>
				<div className="modal-footer">
					<button type="submit" className="btnPrimary">Guardar</button>
					<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
				</div>
			</form>
		</BaseModal>
	);
}

interface ConfigUbicaciones {
	zona: string;
	estanteria: string;
	columnas: string;
	alturas: string;
}
function FormNuevaUbicacion(props: { readonly config: ConfigUbicaciones; readonly setConfig: Setter<ConfigUbicaciones>; }) {
	const { config, setConfig } = props;


	return (
		<div id="divIdUbicacion" >
			<div>
				<label className="labelsForm">Zona</label>
				<select className="inputsForm" name="zona" value={config.zona} onChange={event => { setConfig((newConf) => ({ ...newConf, zona: event.target.value })); }}>
					<option value="A">A</option>
					<option value="B">B</option>
					<option value="C">C</option>
					<option value="D">D</option>
					<option value="E">E</option>
					<option value="F">F</option>
					<option value="G">G</option>
					<option value="H">H</option>
					<option value="I">I</option>
					<option value="J">J</option>
					<option value="K">K</option>
					<option value="L">L</option>
				</select>
			</div>
			<div>
				<label className="labelsForm">Fila</label>
				<input type="number" className="inputsForm" name="fila" value={config.estanteria} onChange={event => { setConfig((oldConfig) => ({ ...oldConfig, estanteria: `${Number(event.target.value)}`.padStart(2, "0") })); }} min={1} />
			</div>
			<div>
				<label className="labelsForm">Columna</label>
				<input type="text" className="inputsForm" name="columna" placeholder="A-#" value={config.columnas} onChange={event => { setConfig((oldConfig) => ({ ...oldConfig, columnas: event.target.value.toUpperCase() })); }} />
			</div>
			<div>
				<label className="labelsForm">Altura</label>
				<input type="text" className="inputsForm" name="altura" placeholder="1-#" value={config.alturas} onChange={event => { setConfig((oldConfig) => ({ ...oldConfig, alturas: event.target.value })); }} />
			</div>
		</div>
	);
}

function Estanteria(props: { readonly zona: string, readonly fila: string; readonly ubicaciones: BasicCeldaInfo[]; readonly setExcluded: Setter<string[]>; }) {
	const onClickUbi = (idUbi: BasicCeldaInfo) => {
		if (idUbi.nueva == true) return;

		props.setExcluded(old => {
			if (old.includes(idUbi.idUbicacion)) {
				return old.filter(oldUbi => oldUbi !== idUbi.idUbicacion);
			} else {
				return [...old, idUbi.idUbicacion];
			}
		});
	};

	return (
		<div style={{ padding: "20px", flex: 1, border: "1px solid #ccc" }}>
			<h1 style={{ textAlign: "center", marginTop: 0 }}>{props.zona}{props.fila}</h1>
			<div className="tablaEstanteria">
				{props.ubicaciones.map(ubicacion => (
					<CeldaEstanteria key={ubicacion.idUbicacion} ubicacion={ubicacion} onClick={() => { onClickUbi(ubicacion); }} />
				))}
			</div>
		</div>
	);
}
function CeldaEstanteria(props: { readonly ubicacion: BasicCeldaInfo, readonly onClick?: () => void; }) {
	let claseFondo = "";
	if (props.ubicacion.nueva == true)
		claseFondo = props.ubicacion.excluded ? "fondoGone hoverBright" : "fondoVerde hoverDark";

	const idColumna = keysColumn.indexOf(props.ubicacion.idColumna) + 1;
	const idAltura = parseInt(props.ubicacion.idAltura, 10) * -1;
	const style = {
		gridColumnStart: String(idColumna),
		gridColumnEnd: String(idColumna),
		gridRowStart: String(idAltura),
		gridRowEnd: String(idAltura),
		cursor: props.ubicacion.nueva == true ? "pointer" : "default"
	};
	return (
		<div className={`celdaUbicacion ${claseFondo}`} style={style} onClick={props.onClick}>
			<h3>{props.ubicacion.idUbicacion}</h3>
		</div>
	);
}


function getUbicacionesVisualizar(config: ConfigUbicaciones, excluded: string[], ubicacionesExistentes: BasicCeldaInfo[]): BasicCeldaInfo[] | null {
	const { zona, estanteria, columnas, alturas } = config;
	const lista: BasicCeldaInfo[] = structuredClone(ubicacionesExistentes);

	const columnasMatch = /^([A-L])-([A-L])$/u.exec(columnas);
	if (!columnasMatch) return null;

	const startColIndex = keysColumn.indexOf(columnasMatch[1]);
	const endColIndex = keysColumn.indexOf(columnasMatch[2]);
	if (startColIndex === -1 || endColIndex === -1 || startColIndex > endColIndex) return null;

	const alturasMatch = /^(\d+)-(\d+)$/u.exec(alturas);
	if (!alturasMatch) return lista;
	const startRowIndex = Number(alturasMatch[1]);
	const endRowIndex = Number(alturasMatch[2]);
	if (startRowIndex === -1 || endRowIndex === -1 || startRowIndex > endRowIndex) return null;

	for (let colIndex = startColIndex; colIndex <= endColIndex; colIndex++) {
		for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
			const idUbicacion = `${zona}${estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;

			if (lista.some(event => event.idUbicacion === idUbicacion)) continue;

			lista.push({
				idUbicacion: idUbicacion,

				idAlmacen: zona,
				idEstanteria: estanteria,
				idColumna: keysColumn[colIndex],
				idAltura: String(rowIndex).padStart(2, "0"),

				nueva: true,
				excluded: excluded.includes(idUbicacion),
			});
		}
	}

	const columnasExistentes = ubicacionesExistentes
		.map(ubi => keysColumn.indexOf(ubi.idColumna))
		.filter(index => index !== -1);

	const alturasExistentes = ubicacionesExistentes
		.map(ubi => Number(ubi.idAltura))
		.filter(altura => Number.isFinite(altura));

	const idsExistentes = new Set(ubicacionesExistentes.map(ubi => ubi.idUbicacion));

	if (columnasExistentes.length > 0 && alturasExistentes.length > 0) {
		const minColExistente = Math.min(...columnasExistentes);
		const maxColExistente = Math.max(...columnasExistentes);
		const minAltExistente = Math.min(...alturasExistentes);
		const maxAltExistente = Math.max(...alturasExistentes);

		for (let colIndex = minColExistente; colIndex <= maxColExistente; colIndex++) {
			for (let rowIndex = minAltExistente; rowIndex <= maxAltExistente; rowIndex++) {
				const idUbicacion = `${zona}${estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;
				const ubicacion = lista.find(ubi => ubi.idUbicacion === idUbicacion);

				if (ubicacion != null) {
					if (ubicacion.nueva === true && !idsExistentes.has(idUbicacion)) {
						ubicacion.excluded = true;
					}
					continue;
				}

				lista.push({
					idUbicacion: idUbicacion,

					idAlmacen: zona,
					idEstanteria: estanteria,
					idColumna: keysColumn[colIndex],
					idAltura: String(rowIndex).padStart(2, "0"),

					nueva: true,
					excluded: true,
				});
			}
		}
	}

	return lista;
}

export type BasicCeldaInfo = {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	articulos: Array<{
		cantidad: number;
		esFijo: boolean;
		idArticulo: string;
		idFase: number;
	}>;

	nueva?: false;
} | {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	nueva: true;
	excluded: boolean;
};